// Replaces the browser context menu inside the Scripture pane. Opens on
// right-click, long-press, or Shift+F10 over a verse; anywhere else the
// browser's own menu is left alone.
//
// The selection is read when the menu opens, not when an item is clicked:
// opening a menu can collapse the selection, and the highlight has to be the
// words the reader actually swept over. With nothing selected, every action
// applies to the whole verse under the pointer.
import { highlightColors, parseRef, toDisplay, toSlug, type HighlightColor } from "@bibleunleashed/shared";
import { useEffect, useRef } from "react";
import { Icon } from "@/components/Icon";
import { selectionToSpans, verseText, verseTextMap } from "./highlights";

/** A span of one verse the menu will act on. */
export interface MenuSpan {
  ref: string;
  startOffset: number;
  endOffset: number;
}

export interface MenuState {
  x: number;
  y: number;
  /** The verse under the pointer, which names the menu and anchors a note. */
  ref: string;
  /** The block the verse is in; offsets are only meaningful against it. */
  block: HTMLElement;
  /** What the reader had selected, or the whole verse when that was nothing. */
  spans: MenuSpan[];
}

/** The verse's own full span, for when nothing is selected. */
function wholeVerse(block: HTMLElement, ref: string): MenuSpan[] {
  const { text } = verseTextMap(block, ref);
  return text ? [{ ref, startOffset: 0, endOffset: text.length }] : [];
}

/**
 * What the menu should act on, given where it was opened. A selection that
 * touches the verse wins; otherwise the verse entire.
 */
function spansFor(block: HTMLElement, ref: string): MenuSpan[] {
  const selection = window.getSelection();
  if (selection && !selection.isCollapsed && selection.rangeCount) {
    const spans = selectionToSpans(block, selection.getRangeAt(0));
    if (spans.length) return spans;
  }
  return wholeVerse(block, ref);
}

/**
 * Bound to the document rather than to the reading pane. The pane does not
 * exist on the first render — a chapter shows "Loading…" until its commentary
 * arrives — and an effect that reads a ref once, with nothing in its
 * dependencies that changes when the element appears, would bind to nothing and
 * never try again. Nothing is suppressed outside a verse either way: every
 * handler starts by looking for one.
 */
export function useContextMenu(setMenu: (m: MenuState | null) => void) {
  useEffect(() => {
    let press: number | undefined;

    const open = (target: Element, x: number, y: number): MenuState | undefined => {
      const v = target.closest<HTMLElement>(".v");
      const block = v?.closest<HTMLElement>(".scripture-block");
      if (!v?.dataset.ref || !block) return undefined;
      return { x, y, ref: v.dataset.ref, block, spans: spansFor(block, v.dataset.ref) };
    };

    const onContext = (e: MouseEvent) => {
      const state = open(e.target as Element, e.clientX, e.clientY);
      if (!state) return;
      e.preventDefault();
      setMenu(state);
    };
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      const target = e.target as Element;
      if (!t || !target.closest(".v")) return;
      press = window.setTimeout(() => {
        const state = open(target, t.clientX, t.clientY);
        if (state) setMenu(state);
      }, 450);
    };
    const cancel = () => window.clearTimeout(press);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
      if (e.key === "F10" && e.shiftKey) {
        const v = document.querySelector<HTMLElement>(".v:hover") ?? document.querySelector<HTMLElement>(".v");
        if (!v) return;
        e.preventDefault();
        const r = v.getBoundingClientRect();
        const state = open(v, r.left + 40, r.bottom);
        if (state) setMenu(state);
      }
    };
    document.addEventListener("contextmenu", onContext);
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    for (const n of ["touchend", "touchmove", "touchcancel"] as const)
      document.addEventListener(n, cancel, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("touchstart", onTouchStart);
      for (const n of ["touchend", "touchmove", "touchcancel"] as const) document.removeEventListener(n, cancel);
      document.removeEventListener("keydown", onKey);
    };
  }, [setMenu]);
}

const SWATCH: Record<HighlightColor, string> = {
  yellow: "var(--hl-y)",
  green: "var(--hl-g)",
  blue: "var(--hl-b)",
  pink: "var(--hl-p)",
};

export interface MenuActions {
  signedIn: boolean;
  highlighted: boolean;
  bookmarked: boolean;
  hasNote: boolean;
  /** Points the study rail at this verse: cross-references, translations, words. */
  onStudy: (ref: string) => void;
  onHighlight: (spans: MenuSpan[], color: HighlightColor) => void;
  onClearHighlight: (ref: string) => void;
  onNote: (ref: string) => void;
  onBookmark: (ref: string) => void;
}

export function ContextMenu({
  menu,
  onClose,
  onToast,
  actions,
}: {
  menu: MenuState;
  onClose: () => void;
  onToast: (msg: string) => void;
  actions: MenuActions;
}) {
  const box = useRef<HTMLDivElement>(null);
  const ref = parseRef(menu.ref);
  const display = toDisplay(ref);
  // "Highlight the two verses you swept over" reads differently from
  // "highlight this verse", and the menu should say which it will do.
  const partial = menu.spans.length > 1 || menu.spans[0]?.startOffset !== 0;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    el.style.left = `${Math.min(menu.x, window.innerWidth - w - 8)}px`;
    el.style.top = `${Math.min(menu.y, window.innerHeight - h - 8)}px`;
    el.querySelector<HTMLButtonElement>("button")?.focus();
    for (const v of menu.block.querySelectorAll(`.v[data-ref="${menu.ref}"]`)) v.classList.add("sel");
    return () => {
      for (const v of menu.block.querySelectorAll(".v.sel")) v.classList.remove("sel");
    };
  }, [menu]);

  const copy = async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onToast(msg);
    } catch {
      onToast("Could not copy");
    }
    onClose();
  };
  const text = () => verseText(menu.block, menu.ref);
  const url = () => `${location.origin}/commentary/${toSlug(ref)}#v${ref.verse}`;
  const item = "flex w-full items-center gap-2.5 rounded-[5px] px-2.5 py-[7px] text-left text-[13.5px] text-ink hover:bg-paper-2";

  const run = (fn: () => void) => {
    fn();
    onClose();
  };

  return (
    <div
      ref={box}
      role="menu"
      className="fixed z-50 min-w-[248px] rounded-lg border border-rule bg-paper p-1.5 shadow-pop"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="mb-1.5 border-b border-rule px-2.5 pt-1.5 pb-2 text-[11px] font-medium tracking-[0.08em] text-ink-3 uppercase">
        {partial ? "Selection" : "Verse"} ·{" "}
        <span className="font-serif text-[12.5px] font-semibold tracking-normal text-accent normal-case">{display}</span>
      </div>

      {actions.signedIn ? (
        <>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5">
            <Icon name="highlighter" width="15" height="15" className="mr-0.5 text-ink-2" />
            {highlightColors.map((color) => (
              <button
                key={color}
                role="menuitem"
                aria-label={`Highlight ${color}`}
                title={`Highlight ${color}`}
                onClick={() => run(() => actions.onHighlight(menu.spans, color))}
                className="size-[22px] rounded-full border border-rule hover:ring-2 hover:ring-ink-3"
                style={{ background: SWATCH[color] }}
              />
            ))}
            {actions.highlighted && (
              <button
                role="menuitem"
                aria-label="Remove highlight"
                title="Remove highlight"
                onClick={() => run(() => actions.onClearHighlight(menu.ref))}
                className="ml-auto grid size-[22px] place-items-center rounded-full text-ink-3 hover:bg-paper-2 hover:text-ink"
              >
                <Icon name="close" width="14" height="14" />
              </button>
            )}
          </div>
          <button role="menuitem" className={item} onClick={() => run(() => actions.onNote(menu.ref))}>
            <Icon name="note" className="text-ink-2" />
            {actions.hasNote ? "Edit your note" : "Write a note"}
          </button>
          <button role="menuitem" className={item} onClick={() => run(() => actions.onBookmark(menu.ref))}>
            <Icon name="bookmark" className="text-ink-2" />
            {actions.bookmarked ? "Remove bookmark" : "Bookmark"}
          </button>
          <hr className="my-1.5 border-rule" />
        </>
      ) : (
        <>
          <a
            href="/login"
            role="menuitem"
            className={`${item} text-ink-2`}
            onClick={onClose}
          >
            <Icon name="highlighter" className="text-ink-3" />
            Sign in to highlight and take notes
          </a>
          <hr className="my-1.5 border-rule" />
        </>
      )}

      <button role="menuitem" className={item} onClick={() => run(() => actions.onStudy(menu.ref))}>
        <Icon name="search" className="text-ink-2" />
        Study this verse
      </button>
      <button role="menuitem" className={item} onClick={() => copy(text(), `Copied ${display}`)}>
        <Icon name="copy" className="text-ink-2" />
        Copy verse
      </button>
      <button
        role="menuitem"
        className={item}
        onClick={() => copy(`${text()} (${display}, ESV)`, `Copied ${display} with reference`)}
      >
        <Icon name="copy" className="text-ink-2" />
        Copy with reference
      </button>
      <hr className="my-1.5 border-rule" />
      <button role="menuitem" className={item} onClick={() => copy(url(), "Link copied")}>
        <Icon name="link" className="text-ink-2" />
        Share link
      </button>
    </div>
  );
}
